import { GeoComplyCheckResult, GeoComplyFailureReason } from '@/hooks/useGeoComply';
import { toast } from 'sonner';

/**
 * Map of failure reasons to user-friendly descriptions for toast messages
 */
const FAILURE_REASON_DESCRIPTIONS: Record<GeoComplyFailureReason, string> = {
  boundary: 'Your location is outside the permitted area',
  proxy: 'VPN or proxy detected',
  vpn: 'VPN detected',
  devtools: 'Browser developer tools or extension detected',
  location_unavailable: 'Location could not be determined',
  unknown: 'Location verification issue',
};

/**
 * Build a user-friendly description of all failure reasons
 */
function buildFailureDescription(reasons?: GeoComplyFailureReason[], location?: GeoComplyCheckResult['location']): string {
  const parts: string[] = [];
  
  // Add location info if available
  if (location?.country) {
    parts.push(`Detected: ${location.country}${location.region ? `, ${location.region}` : ''}`);
  }
  
  // Add failure reasons
  if (reasons && reasons.length > 0) {
    // Deduplicate vpn/proxy
    const uniqueReasons = reasons.filter((reason, index, arr) => {
      if (reason === 'vpn' && arr.includes('proxy')) return false;
      return arr.indexOf(reason) === index;
    });
    
    const reasonDescriptions = uniqueReasons
      .map(r => FAILURE_REASON_DESCRIPTIONS[r])
      .filter(Boolean);
    
    if (reasonDescriptions.length > 0) {
      parts.push(reasonDescriptions.join('; '));
    }
  }
  
  return parts.length > 0 ? parts.join('. ') : '';
}

/**
 * Helper function for performing geolocation checks with user-friendly error handling
 * 
 * This utility function wraps the geolocation check and provides:
 * - User-friendly toast notifications for errors
 * - Consistent error handling across all integration points
 * - Location-specific error messages with ALL failure reasons
 * 
 * USAGE:
 * This function is used at all integration points:
 * - Registration: performGeolocationCheck(checkGeolocation, 'register')
 * - Login: performGeolocationCheck(checkGeolocation, 'login')
 * - Purchase: performGeolocationCheck(checkGeolocation, 'make a purchase')
 * - Redemption: performGeolocationCheck(checkGeolocation, 'create a redemption request')
 * 
 * ERROR HANDLING:
 * - If check fails (network error, SDK error): Shows generic error message
 * - If location is non-compliant: Shows location-specific error with ALL detected reasons
 * - Returns false to block the action in both error cases
 * 
 * @param checkGeolocation - The check function from useGeoComply hook
 * @param action - The action being performed (e.g., 'register', 'login', 'make a purchase')
 *                 Used in error messages to inform user why the check was performed
 * @returns Promise<boolean> - true if location is compliant, false otherwise
 */
export async function performGeolocationCheck(
  checkGeolocation: () => Promise<GeoComplyCheckResult>,
  action: string = 'continue'
): Promise<boolean> {

  try {
    // Perform the geolocation check
    const result = await checkGeolocation();
    
    // Handle check failure (network error, SDK error, etc.)
    if (!result.success) {
      // Check if this is an SDK error (like error 614) vs a real failure
      // SDK errors might still have been processed by the backend
      const isSdkError = result.error?.includes('code 614') || 
                        result.error?.includes('unexpected error') ||
                        result.error?.includes('Geolocation check failed with code');
      
      if (isSdkError && result.isCompliant === undefined) {
        // SDK error but compliance status is unknown - might have been processed
        // Allow user to proceed with a warning (backend might have already processed it)
        console.warn('[GeoComply] SDK error occurred, but backend may have processed the request', {
          error: result.error,
          timestamp: new Date().toISOString(),
        });
        toast.warning('Location Check Warning', {
          description: `Location verification encountered an issue, but you may proceed. If problems persist, please contact support.`,
        });
        return true; // Allow to proceed - SDK error doesn't mean non-compliant
      }
      
      // Build description with any available failure reasons
      const failureDesc = buildFailureDescription(result.failureReasons, result.location);
      
      toast.error('Location Check Failed', {
        description: failureDesc || `Unable to verify your location. Please ensure location services are enabled and try again.`,
      });
      return false; // Block the action - fail securely
    }

    // Handle non-compliant location (only check if isCompliant is explicitly false)
    // undefined means unknown/error, which we handle above
    if (result.isCompliant === false) {
      // Build detailed failure description with all reasons
      const failureDesc = buildFailureDescription(result.failureReasons, result.location);
      
      // Determine the title based on failure reasons
      let title = 'Location Not Permitted';
      if (result.failureReasons?.includes('proxy')) {
        title = 'VPN or Proxy Detected';
      } else if (result.failureReasons?.includes('devtools')) {
        title = 'Browser Extension Detected';
      } else if (result.failureReasons?.includes('location_unavailable')) {
        title = 'Location Unavailable';
      }
      
      // Show user-friendly error with all failure reasons
      toast.error(title, {
        description: failureDesc 
          ? `Unable to ${action}. ${failureDesc}.`
          : `Your current location is not permitted to ${action}.`,
      });
      return false; // Block the action - location not allowed
    }

    // Location is compliant - allow the action to proceed
    return true;
  } catch (error: any) {
    // Handle unexpected errors (shouldn't happen, but handle gracefully)
    console.error('Geolocation check error:', error);
    toast.error('Location Check Failed', {
      description: error.message || 'An error occurred while checking your location. Please try again.',
    });
    return false; // Block the action - fail securely
  }
}

